import { usePersistedState } from "@/lib/persistedFilters";
// Iter 91aj — Humi People (directory). Data is scoped server-side:
//   • HR / super_admin → sees everyone with expanded fields
//   • Managers         → sees self + direct reports
//   • Other staff      → sees themselves only
// So the frontend just renders whatever the API returned; no client-side gating.

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Mail, Phone, Cake, Award, ShieldCheck, UsersIcon as UsersIconLucide, Users as Users2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatBirthday, formatAnniversary } from "@/lib/celebrations";
import { HumiDatesGrid } from "@/components/HumiDatesGrid";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Avatar } from "@/components/Avatar";
import { toast } from "sonner";

export default function HumiPeople() {
  const [params] = useSearchParams();
  const [people, setPeople] = useState({ items: [], is_hr: false });
  const [q, setQ] = usePersistedState("humi-people.q", "");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/humi/people", { params: q ? { search: q } : {} });
        setPeople(data);
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const grouped = useMemo(() => {
    const teams = {};
    (people.items || []).forEach((p) => {
      const key = p.team || "Unassigned";
      (teams[key] = teams[key] || []).push(p);
    });
    return Object.entries(teams).sort((a, b) => a[0].localeCompare(b[0]));
  }, [people]);

  return (
    <div className="space-y-4" data-testid="humi-people">
      <PageHeader
        icon={<Users2 className="h-5 w-5 text-primary" />}
        title="People"
        subtitle={people.is_hr ? `Full staff directory (${people.items.length})` : "Your team"}
      />

      <Card className="p-4 border-slate-200 dark:border-slate-700">
        <div className="relative max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="humi-people-search"
            className="pl-9"
            placeholder="Search by name or email"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </Card>

      {loading && (
        <Card className="p-10 text-center text-slate-400 border-slate-200 dark:border-slate-700">Loading…</Card>
      )}

      {!loading && grouped.length === 0 && (
        <Card className="p-10 text-center text-slate-400 border-slate-200 dark:border-slate-700" data-testid="humi-people-empty">
          No matching staff found.
        </Card>
      )}

      {people.is_hr && <HumiDatesGrid defaultOnly={params.get("dates") === "missing"} />}

      {grouped.map(([team, members]) => (
        <Card key={team} className="p-0 overflow-hidden border-slate-200 dark:border-slate-700" data-testid={`humi-people-team-${team.replace(/\s+/g,'-').toLowerCase()}`}>
          <div className="border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 px-4 py-2">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
              <UsersIconLucide className="h-4 w-4" /> {team} <span className="text-xs text-slate-400">{members.length}</span>
            </div>
          </div>
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {members.map((p) => (
              <li key={p.user_id} data-testid={`humi-person-${p.user_id}`}
                  className="flex flex-wrap items-center gap-3 px-4 py-3">
                <Avatar name={p.name} src={p.avatar_url} className="h-10 w-10 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-slate-800 dark:text-slate-100 truncate">{p.name}</div>
                    {p.is_manager && (
                      <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">Manager</span>
                    )}
                    {p.qualification && (
                      <span className="rounded bg-emerald-50 dark:bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">{p.qualification}</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{p.role_label}{p.team ? ` · ${p.team}` : ""}</div>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600 dark:text-slate-300">
                  <a href={`mailto:${p.email}`} className="inline-flex items-center gap-1 hover:text-primary" data-testid={`humi-person-email-${p.user_id}`}>
                    <Mail className="h-3.5 w-3.5" /> {p.email}
                  </a>
                  {p.phone && (
                    <a href={`tel:${p.phone}`} className="inline-flex items-center gap-1 hover:text-primary">
                      <Phone className="h-3.5 w-3.5" /> {p.phone}
                    </a>
                  )}
                  {p.birthday && (
                    <span className="inline-flex items-center gap-1 text-slate-500 dark:text-slate-400"
                          data-testid={`humi-person-birthday-${p.user_id}`} title="Birthday">
                      <Cake className="h-3.5 w-3.5" /> {formatBirthday(p.birthday)}
                    </span>
                  )}
                  {(() => {
                    const a = formatAnniversary(p.work_anniversary);
                    if (!a) return null;
                    return (
                      <span className="inline-flex items-center gap-1 text-slate-500 dark:text-slate-400"
                            data-testid={`humi-person-anniversary-${p.user_id}`}
                            title={`Started ${a.dateLabel}`}>
                        <Award className="h-3.5 w-3.5" /> {a.dateLabel}
                        <span className="rounded bg-slate-100 dark:bg-slate-800 px-1 py-0.5 text-[10px] font-semibold text-slate-600 dark:text-slate-300">
                          {a.years} yr{a.years === 1 ? "" : "s"}
                        </span>
                      </span>
                    );
                  })()}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      ))}

      {people.is_hr && (
        <Card className="p-3 border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/40 dark:bg-emerald-500/10">
          <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-300">
            <ShieldCheck className="h-4 w-4" /> HR view — you can see all staff and confidential fields.
          </div>
        </Card>
      )}
    </div>
  );
}
